/**
 * User Management Tab Component
 * Admin interface for managing users, roles, and permissions
 * Supports server-side pagination, search, and sorting
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Users, Shield, ShieldCheck, RefreshCw, X, Trash2, School, ArrowUpDown, ArrowUp, ArrowDown, Search, ChevronLeft, ChevronRight, AlertTriangle, Pencil, Eye, EyeOff, GraduationCap, Loader2, Mail, Upload, Download, UserPlus, BookCheck, Ban, CheckCircle, Clock, CreditCard } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { SubscriptionSummary, TeacherPlanBadge } from "./SubscriptionBadges";
import { StripeSyncDialog } from "./StripeSyncDialog";

const API = '/api';
const PER_PAGE = 50;

const formatJoined = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" });
};

export default function UserManagementTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, userId: null, userName: null });
  const [deleting, setDeleting] = useState(false);
  const [editScoreModal, setEditScoreModal] = useState({ open: false, userId: null, userName: null, currentScore: 0 });
  const [newScore, setNewScore] = useState("");
  const [savingScore, setSavingScore] = useState(false);
  const [editCaveBatModal, setEditCaveBatModal] = useState({ open: false, userId: null, userName: null, currentScore: 0 });
  const [newCaveBatScore, setNewCaveBatScore] = useState("");
  const [savingCaveBatScore, setSavingCaveBatScore] = useState(false);
  const [schoolAdminModal, setSchoolAdminModal] = useState({ open: false, userId: null, userName: null, userEmail: null });
  const [schoolName, setSchoolName] = useState("");
  const [savingSchoolAdmin, setSavingSchoolAdmin] = useState(false);
  const [suspendModal, setSuspendModal] = useState({ open: false, userId: null, userName: null, currentUntil: null });
  const [stripeModal, setStripeModal] = useState({ open: false, userId: null, userLabel: "" });
  const [suspendUntil, setSuspendUntil] = useState("");
  const [savingSuspend, setSavingSuspend] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [hideTestUsers, setHideTestUsers] = useState(true);
  const [testUserCount, setTestUserCount] = useState(0);
  const [totalAllUsers, setTotalAllUsers] = useState(0);
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  
  // Invite state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [bulkInviting, setBulkInviting] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const fileInputRef = useRef(null);
  
  // Completed questions modal state
  const [completedQuestionsModal, setCompletedQuestionsModal] = useState({ 
    open: false, 
    userId: null, 
    userName: null 
  });
  const [completedQuestionsData, setCompletedQuestionsData] = useState(null);
  const [loadingCompletedQuestions, setLoadingCompletedQuestions] = useState(false);

  // Fetch users with pagination, search, and sorting
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: PER_PAGE.toString(),
        sort_by: sortField,
        sort_order: sortDirection,
        exclude_test: hideTestUsers.toString(),
      });
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const response = await fetch(`${API}/admin/users?${params}`, { 
        credentials: "include" 
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotalPages(data.total_pages || 1);
        setTotalUsers(data.total || 0);
        setTestUserCount(data.test_user_count || 0);
        setTotalAllUsers(data.total_all || 0);
        setSubscriptionSummary(data.subscription_summary || null);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [page, sortField, sortDirection, searchQuery, hideTestUsers]);

  // Handle test user toggle
  const handleToggleTestUsers = () => {
    setHideTestUsers(!hideTestUsers);
    setPage(1); // Reset to first page when toggling
  };

  // Fetch pending invites
  const fetchPendingInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const response = await fetch(`${API}/admin/pending-invites`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPendingInvites(data.invites || []);
      }
    } catch (error) {
      console.error("Error fetching pending invites:", error);
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  // Handle single invite
  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    
    setInviting(true);
    try {
      const response = await fetch(`${API}/admin/invite-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: inviteEmail.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.skipped) {
          toast.warning(data.message);
        } else if (data.email_sent === false) {
          toast.warning(`Invitation created for ${inviteEmail}, but email failed to send. Check your email configuration.`);
          setInviteEmail("");
          fetchPendingInvites();
        } else {
          toast.success(`Invitation sent to ${inviteEmail}`);
          setInviteEmail("");
          fetchPendingInvites();
        }
      } else {
        toast.error(data.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Error sending invitation");
    } finally {
      setInviting(false);
    }
  };

  // Handle bulk CSV invite
  const handleBulkInvite = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setBulkInviting(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/admin/invite-users/bulk`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        let message = `Invited: ${data.invited_count}`;
        if (data.skipped_count > 0) message += `, Skipped: ${data.skipped_count}`;
        if (data.failed_count > 0) message += `, Failed: ${data.failed_count}`;
        
        if (data.invited_count > 0) {
          toast.success(message);
        } else if (data.skipped_count > 0) {
          toast.warning(message);
        } else {
          toast.error(message);
        }
        
        fetchPendingInvites();
      } else {
        toast.error(data.detail || "Failed to process CSV");
      }
    } catch (error) {
      toast.error("Error uploading CSV");
    } finally {
      setBulkInviting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Download CSV template
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API}/admin/invite-users/template`, { credentials: "include" });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "platform_invite_template.csv";
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  // Cancel pending invite
  const handleCancelInvite = async (inviteToken) => {
    try {
      const response = await fetch(`${API}/admin/pending-invites/${inviteToken}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Invite cancelled");
        fetchPendingInvites();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to cancel invite");
      }
    } catch (error) {
      toast.error("Error cancelling invite");
    }
  };

  // Handle search with debounce

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setPage(1); // Reset to first page on new search
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(1); // Reset to first page on sort change
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const SortableHead = ({ field, className = "", testId, children }) => (
    <TableHead className={`cursor-pointer hover:bg-muted/50 select-none ${className}`} onClick={() => handleSort(field)} data-testid={testId}>
      <div className="flex items-center whitespace-nowrap">{children}<SortIcon field={field} /></div>
    </TableHead>
  );

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (response.ok) {
        toast.success("User role updated");
        fetchUsers();
      } else {
        toast.error("Failed to update role");
      }
    } catch (error) {
      toast.error("Error updating role");
    }
  };

  const handleToggleSubmissionBan = async (userId, userName, currentlyBanned) => {
    const newStatus = currentlyBanned ? "allowed" : "banned";
    const action = currentlyBanned ? "allow submissions for" : "ban submissions for";
    
    if (!window.confirm(`Are you sure you want to ${action} "${userName}"?`)) return;
    
    try {
      const response = await fetch(`${API}/admin/users/${userId}/submission-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        toast.success(currentlyBanned ? `Submissions allowed for ${userName}` : `${userName} banned from submitting`);
        fetchUsers();
      } else {
        toast.error("Failed to update submission status");
      }
    } catch (error) {
      toast.error("Error updating submission status");
    }
  };

  const openSuspendModal = (userId, userName, currentUntil) => {
    setSuspendModal({ open: true, userId, userName, currentUntil });
    // Pre-fill with existing future suspension date, else default to +7 days
    let prefill = "";
    if (currentUntil && new Date(currentUntil) > new Date()) {
      prefill = new Date(currentUntil).toISOString().slice(0, 10);
    } else {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      prefill = d.toISOString().slice(0, 10);
    }
    setSuspendUntil(prefill);
  };

  const confirmSuspend = async (clear = false) => {
    const { userId, userName } = suspendModal;
    if (!clear && !suspendUntil) {
      toast.error("Please choose a suspension end date");
      return;
    }
    setSavingSuspend(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/suspension`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(clear ? { until: null } : { until: suspendUntil }),
      });
      const data = await response.json();
      if (response.ok) {
        if (clear) {
          toast.success(`Suspension lifted for ${userName}`);
        } else {
          toast.success(`${userName} suspended until ${new Date(data.suspended_until).toLocaleDateString()}`);
        }
        fetchUsers();
        setSuspendModal({ open: false, userId: null, userName: null, currentUntil: null });
        setSuspendUntil("");
      } else {
        toast.error(data.detail || "Failed to update suspension");
      }
    } catch (error) {
      toast.error("Error updating suspension");
    } finally {
      setSavingSuspend(false);
    }
  };

  const handleDeleteUser = async (userId, userName, subscriptionTier, subscriptionStatus) => {
    const hasActiveSubscription = subscriptionTier && subscriptionTier !== "free" && subscriptionStatus === "active";
    setDeleteModal({ open: true, userId, userName, hasActiveSubscription, subscriptionTier });
  };

  const confirmDeleteUser = async () => {
    const { userId, userName } = deleteModal;
    setDeleting(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.subscription_cancelled) {
          toast.success(`User "${userName}" deleted. Their ${data.subscription_info?.tier} subscription was cancelled.`);
        } else {
          toast.success(`User "${userName}" deleted successfully`);
        }
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Error deleting user");
    } finally {
      setDeleting(false);
      setDeleteModal({ open: false, userId: null, userName: null, hasActiveSubscription: false, subscriptionTier: null });
    }
  };

  const handleEditScore = (userId, userName, currentScore) => {
    setEditScoreModal({ open: true, userId, userName, currentScore });
    setNewScore(currentScore.toString());
  };

  const confirmEditScore = async () => {
    const { userId, userName, currentScore } = editScoreModal;
    const scoreValue = parseInt(newScore, 10);
    
    if (isNaN(scoreValue) || scoreValue < 0) {
      toast.error("Please enter a valid score (0 or greater)");
      return;
    }
    
    setSavingScore(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/score`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ total_score: scoreValue })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Score updated: ${currentScore} → ${scoreValue}`);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update score");
      }
    } catch (error) {
      toast.error("Error updating score");
    } finally {
      setSavingScore(false);
      setEditScoreModal({ open: false, userId: null, userName: null, currentScore: 0 });
      setNewScore("");
    }
  };

  const handleMakeSchoolAdmin = (userId, userName, userEmail) => {
    setSchoolAdminModal({ open: true, userId, userName, userEmail });
    setSchoolName("");
  };

  const handleEditCaveBatScore = (userId, userName, currentScore) => {
    setEditCaveBatModal({ open: true, userId, userName, currentScore: currentScore || 0 });
    setNewCaveBatScore((currentScore || 0).toString());
  };

  const confirmEditCaveBatScore = async () => {
    const { userId, userName, currentScore } = editCaveBatModal;
    const scoreValue = parseInt(newCaveBatScore, 10);
    
    if (isNaN(scoreValue) || scoreValue < 0) {
      toast.error("Please enter a valid score (0 or greater)");
      return;
    }
    
    setSavingCaveBatScore(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/cave-bat-score`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ score: scoreValue })
      });
      
      if (response.ok) {
        toast.success(`Cave Bat score updated: ${currentScore} → ${scoreValue}`);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update Cave Bat score");
      }
    } catch (error) {
      toast.error("Error updating Cave Bat score");
    } finally {
      setSavingCaveBatScore(false);
      setEditCaveBatModal({ open: false, userId: null, userName: null, currentScore: 0 });
      setNewCaveBatScore("");
    }
  };

  const confirmMakeSchoolAdmin = async () => {
    const { userId, userName } = schoolAdminModal;
    
    if (!schoolName.trim()) {
      toast.error("Please enter a school name");
      return;
    }
    
    setSavingSchoolAdmin(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/make-school-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ school_name: schoolName.trim() })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${userName} is now a school admin for "${schoolName}"`);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to make school admin");
      }
    } catch (error) {
      toast.error("Error making school admin");
    } finally {
      setSavingSchoolAdmin(false);
      setSchoolAdminModal({ open: false, userId: null, userName: null, userEmail: null });
      setSchoolName("");
    }
  };

  // Fetch completed questions for a user
  const fetchCompletedQuestions = async (userId, userName) => {
    setCompletedQuestionsModal({ open: true, userId, userName });
    setLoadingCompletedQuestions(true);
    setCompletedQuestionsData(null);
    
    try {
      const response = await fetch(`${API}/admin/users/${userId}/completed-questions`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompletedQuestionsData(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to load completed questions");
        setCompletedQuestionsModal({ open: false, userId: null, userName: null });
      }
    } catch (error) {
      toast.error("Error loading completed questions");
      setCompletedQuestionsModal({ open: false, userId: null, userName: null });
    } finally {
      setLoadingCompletedQuestions(false);
    }
  };

  const startIndex = (page - 1) * PER_PAGE + 1;
  const endIndex = Math.min(page * PER_PAGE, totalUsers);

  return (
    <div className="bg-card rounded-2xl border border-border/40 p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-secondary/10">
            <Users className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg">User Management</h2>
            <p className="text-sm text-muted-foreground">
              {totalAllUsers} users total
              {testUserCount > 0 && (
                <span className="text-muted-foreground/70"> ({testUserCount} test users{hideTestUsers ? " hidden" : ""})</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Invite Users Button */}
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setInviteModalOpen(true);
              fetchPendingInvites();
            }}
            className="gap-2 whitespace-nowrap"
            data-testid="invite-users-btn"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Invite Users</span>
          </Button>
          {/* Test Users Toggle */}
          <Button
            variant={hideTestUsers ? "outline" : "secondary"}
            size="sm"
            onClick={handleToggleTestUsers}
            className="gap-2 whitespace-nowrap"
            title={hideTestUsers ? "Show test users" : "Hide test users"}
            data-testid="toggle-test-users-btn"
          >
            {hideTestUsers ? (
              <>
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Show Test</span>
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4" />
                <span className="hidden sm:inline">Hide Test</span>
              </>
            )}
          </Button>
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search email, name, nickname..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
              data-testid="user-search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={fetchUsers}
            disabled={loading}
            className="gap-2"
            data-testid="refresh-users-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      <SubscriptionSummary summary={subscriptionSummary} />

      <div className="overflow-x-auto">
        <Table className="table-fixed min-w-[1100px] xl:min-w-0" data-testid="users-table">
          <TableHeader>
            <TableRow>
              <SortableHead field="nickname" className="w-[13%]">Nickname</SortableHead>
              <TableHead className="w-[10%]">Name</TableHead>
              <SortableHead field="email" className="w-[14%]">Email</SortableHead>
              <SortableHead field="role" className="w-[7%]">Role</SortableHead>
              <SortableHead field="total_score" className="w-[8%]">Score</SortableHead>
              <SortableHead field="subscription_tier" className="w-[10%]">Plan</SortableHead>
              <TableHead className="w-[6%] whitespace-nowrap">🦇 Bat</TableHead>
              <TableHead className="w-[9%]">School</TableHead>
              <SortableHead field="created_at" className="w-[7%]" testId="sort-joined">Joined</SortableHead>
              <TableHead className="w-[16%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  {searchQuery ? `No users found matching "${searchQuery}"` : hideTestUsers && testUserCount > 0 ? `No non-test users found (${testUserCount} test users hidden)` : "No users found"}
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.user_id} className={u.submission_banned ? "bg-red-500/5" : (u.suspended_until && new Date(u.suspended_until) > new Date() ? "bg-amber-500/5" : "")}>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-primary truncate" title={u.nickname || ""}>{u.nickname || "—"}</span>
                      {u.submission_banned && (
                        <Badge variant="destructive" className="text-xs px-1 py-0">
                          <Ban className="w-3 h-3 mr-1" />
                          Banned
                        </Badge>
                      )}
                      {!u.submission_banned && u.suspended_until && new Date(u.suspended_until) > new Date() && (
                        <Badge className="text-xs px-1 py-0 bg-amber-500/20 text-amber-600 border-amber-500/40" data-testid={`suspended-badge-${u.user_id}`}>
                          <Clock className="w-3 h-3 mr-1" />
                          Until {new Date(u.suspended_until).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-6 h-6 rounded-full shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Users className="w-3 h-3 text-muted-foreground" />
                        </div>
                      )}
                      <span className="text-sm truncate" title={u.name || ""}>{u.name || "—"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm truncate" title={u.email}>{u.email}</TableCell>
                  <TableCell>
                    {u.role === "master_admin" ? (
                      <Badge className="bg-secondary text-secondary-foreground whitespace-nowrap" title="Master Admin">
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        Master
                      </Badge>
                    ) : u.role === "admin" ? (
                      <Badge variant="outline" className="text-primary border-primary">
                        <Shield className="w-3 h-3 mr-1" />
                        Admin
                      </Badge>
                    ) : (
                      <Badge variant="outline">User</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      <span>{u.total_score || 0} pts</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleEditScore(u.user_id, u.name || u.email, u.total_score || 0)}
                        title="Edit score"
                        data-testid={`edit-score-${u.user_id}`}
                      >
                        <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.school_admin ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-purple-500/20 text-purple-600 border-purple-500/30">
                          <GraduationCap className="w-3 h-3 mr-1" />
                          School Admin
                        </Badge>
                        <TeacherPlanBadge tier={u.subscription_tier} userId={u.user_id} />
                      </div>
                    ) : u.subscription_tier === "teacher_plus" || u.subscription_tier === "teacher_pro" ? (
                      <TeacherPlanBadge tier={u.subscription_tier} userId={u.user_id} />
                    ) : u.subscription_tier === "school" ? (
                      <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30">
                        <School className="w-3 h-3 mr-1" />
                        School
                      </Badge>
                    ) : u.subscription_tier === "student" ? (
                      <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
                        Student
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">Free</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className="font-medium">{u.cave_bat_score || 0}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleEditCaveBatScore(u.user_id, u.name || u.email, u.cave_bat_score || 0)}
                        title="Edit Cave Bat score"
                        data-testid={`edit-cave-bat-${u.user_id}`}
                      >
                        <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.school_admin && u.admin_school_name ? (
                      <div className="flex items-center gap-1.5">
                        <GraduationCap className="w-3.5 h-3.5 text-purple-500" />
                        <span className="text-sm font-medium text-purple-600 dark:text-purple-400 truncate" title={u.admin_school_name}>
                          {u.admin_school_name}
                        </span>
                      </div>
                    ) : u.schools && u.schools.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {u.schools.map((s, idx) => (
                          <span key={s.school_id} className="text-sm text-muted-foreground truncate" title={s.school_name}>
                            {s.school_name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap" title={u.created_at ? new Date(u.created_at).toLocaleString("en-AU") : ""} data-testid={`joined-${u.user_id}`}>
                    {formatJoined(u.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {u.role !== "master_admin" ? (
                      <div className="flex justify-end gap-0.5">
                        <Select
                          value={u.role}
                          onValueChange={(value) => handleUpdateUserRole(u.user_id, value)}
                        >
                          <SelectTrigger className="w-[76px] h-8 px-2 text-xs" data-testid={`role-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        {!u.school_admin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleMakeSchoolAdmin(u.user_id, u.nickname || u.name || u.email, u.email)}
                            title="Make school admin"
                            data-testid={`make-school-admin-btn-${u.user_id}`}
                          >
                            <GraduationCap className="w-4 h-4 text-purple-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => fetchCompletedQuestions(u.user_id, u.nickname || u.name || u.email)}
                          title="View completed questions"
                          data-testid={`view-completed-${u.user_id}`}
                        >
                          <BookCheck className="w-4 h-4 text-emerald-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleToggleSubmissionBan(u.user_id, u.nickname || u.name || u.email, u.submission_banned)}
                          title={u.submission_banned ? "Allow submissions" : "Ban submissions"}
                          data-testid={`toggle-ban-${u.user_id}`}
                        >
                          {u.submission_banned ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Ban className="w-4 h-4 text-red-500" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openSuspendModal(u.user_id, u.nickname || u.name || u.email, u.suspended_until)}
                          title={u.suspended_until && new Date(u.suspended_until) > new Date() ? "Edit / lift suspension" : "Suspend from submitting"}
                          data-testid={`suspend-user-${u.user_id}`}
                        >
                          <Clock className={`w-4 h-4 ${u.suspended_until && new Date(u.suspended_until) > new Date() ? "text-amber-500" : "text-muted-foreground"}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setStripeModal({ open: true, userId: u.user_id, userLabel: u.email || u.nickname || u.name })}
                          title="Stripe: sync plan, cancel / refund duplicates"
                          data-testid={`stripe-user-${u.user_id}`}
                        >
                          <CreditCard className={`w-4 h-4 ${u.subscription_tier && u.subscription_tier !== "free" ? "text-primary" : "text-muted-foreground"}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteUser(u.user_id, u.name, u.subscription_tier, u.subscription_status)}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Protected</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/40">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex}-{endIndex} of {totalUsers} users
            {hideTestUsers && testUserCount > 0 && ` (${testUserCount} test users hidden)`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm px-2">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              data-testid="next-page-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !deleting && setDeleteModal({ open, userId: null, userName: null, hasActiveSubscription: false, subscriptionTier: null })}>
        <DialogContent className="sm:max-w-md" data-testid="delete-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold text-foreground">"{deleteModal.userName}"</span>?
              
              {deleteModal.hasActiveSubscription && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-600 dark:text-amber-400 font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Active Subscription Warning
                  </p>
                  <p className="text-sm mt-1">
                    This user has an active <span className="font-semibold capitalize">{deleteModal.subscriptionTier}</span> subscription. 
                    Their Stripe subscription will be <span className="font-semibold">automatically cancelled</span> when deleted.
                  </p>
                </div>
              )}
              
              <div className="mt-4">
                This will permanently remove:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>User account and profile</li>
                  <li>All session data</li>
                  <li>Score and progress history</li>
                  {deleteModal.hasActiveSubscription && (
                    <li className="text-amber-600 dark:text-amber-400">Active Stripe subscription</li>
                  )}
                </ul>
              </div>
              <p className="mt-4 text-destructive font-medium">This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteModal({ open: false, userId: null, userName: null, hasActiveSubscription: false, subscriptionTier: null })}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteUser}
              disabled={deleting}
              data-testid="confirm-delete-user-btn"
            >
              {deleting ? "Deleting..." : deleteModal.hasActiveSubscription ? "Cancel Subscription & Delete" : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Score Modal */}
      <Dialog open={editScoreModal.open} onOpenChange={(open) => !savingScore && setEditScoreModal({ open, userId: null, userName: null, currentScore: 0 })}>
        <DialogContent className="sm:max-w-md" data-testid="edit-score-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit User Score
            </DialogTitle>
            <DialogDescription>
              Editing score for <span className="font-semibold text-foreground">"{editScoreModal.userName}"</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium mb-2">
              Current Score: <span className="text-muted-foreground">{editScoreModal.currentScore} pts</span>
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                value={newScore}
                onChange={(e) => setNewScore(e.target.value)}
                placeholder="Enter new score"
                className="flex-1"
                data-testid="edit-score-input"
              />
              <span className="text-muted-foreground">pts</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Enter the new total score for this user. Score cannot be negative.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setEditScoreModal({ open: false, userId: null, userName: null, currentScore: 0 });
                setNewScore("");
              }}
              disabled={savingScore}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmEditScore}
              disabled={savingScore || newScore === "" || parseInt(newScore, 10) === editScoreModal.currentScore}
              data-testid="confirm-edit-score-btn"
            >
              {savingScore ? "Saving..." : "Save Score"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Cave Bat Score Modal */}
      <Dialog open={editCaveBatModal.open} onOpenChange={(open) => !savingCaveBatScore && setEditCaveBatModal({ open, userId: null, userName: null, currentScore: 0 })}>
        <DialogContent className="sm:max-w-md" data-testid="edit-cave-bat-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🦇 Edit Cave Bat Score
            </DialogTitle>
            <DialogDescription>
              Editing Cave Bat score for <span className="font-semibold text-foreground">"{editCaveBatModal.userName}"</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-3">
              Current Score: <span className="text-foreground font-medium">{editCaveBatModal.currentScore}</span>
            </p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                value={newCaveBatScore}
                onChange={(e) => setNewCaveBatScore(e.target.value)}
                placeholder="Enter new score"
                className="flex-1"
                data-testid="edit-cave-bat-input"
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              This score will appear on the Cave Bat rankings page.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setEditCaveBatModal({ open: false, userId: null, userName: null, currentScore: 0 });
                setNewCaveBatScore("");
              }}
              disabled={savingCaveBatScore}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmEditCaveBatScore}
              disabled={savingCaveBatScore || newCaveBatScore === "" || parseInt(newCaveBatScore, 10) === editCaveBatModal.currentScore}
              data-testid="confirm-cave-bat-btn"
            >
              {savingCaveBatScore ? "Saving..." : "Save Score"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Make School Admin Modal */}
      <Dialog open={schoolAdminModal.open} onOpenChange={(open) => !savingSchoolAdmin && setSchoolAdminModal({ open, userId: null, userName: null, userEmail: null })}>
        <DialogContent className="sm:max-w-md" data-testid="school-admin-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-purple-500" />
              Make School Admin
            </DialogTitle>
            <DialogDescription>
              Grant school admin access to <span className="font-semibold text-foreground">"{schoolAdminModal.userName}"</span>
              {schoolAdminModal.userEmail && (
                <span className="block text-xs mt-1">({schoolAdminModal.userEmail})</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="school-name" className="block text-sm font-medium mb-2">
                School Name
              </Label>
              <Input
                id="school-name"
                type="text"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                placeholder="Enter school name"
                className="w-full"
                data-testid="school-name-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              This will create a new school and make this user the school admin. They will be able to invite and manage students.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setSchoolAdminModal({ open: false, userId: null, userName: null, userEmail: null });
                setSchoolName("");
              }}
              disabled={savingSchoolAdmin}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmMakeSchoolAdmin}
              disabled={savingSchoolAdmin || !schoolName.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-school-admin-btn"
            >
              {savingSchoolAdmin ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Make School Admin"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Users Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Invite Users to Platform
            </DialogTitle>
            <DialogDescription>
              Invite new users to CodingDrills.au. Already registered users will be skipped.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="single" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="single" className="gap-1">
                <Mail className="w-4 h-4" />
                Single
              </TabsTrigger>
              <TabsTrigger value="bulk" className="gap-1">
                <Upload className="w-4 h-4" />
                Bulk CSV
              </TabsTrigger>
              <TabsTrigger value="pending" className="gap-1">
                <RefreshCw className="w-4 h-4" />
                Pending ({pendingInvites.length})
              </TabsTrigger>
            </TabsList>
            
            {/* Single Invite Tab */}
            <TabsContent value="single" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address</Label>
                <div className="flex gap-2">
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="user@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleInviteUser()}
                    data-testid="invite-email-input"
                  />
                  <Button
                    onClick={handleInviteUser}
                    disabled={inviting || !inviteEmail.trim()}
                    data-testid="send-invite-btn"
                  >
                    {inviting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                An invitation email will be sent with a link to register.
              </p>
            </TabsContent>
            
            {/* Bulk Invite Tab */}
            <TabsContent value="bulk" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Upload CSV File</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </Button>
                </div>
                
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleBulkInvite}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    {bulkInviting ? (
                      <>
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Processing...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-muted-foreground" />
                        <span className="text-sm font-medium">Click to upload CSV</span>
                        <span className="text-xs text-muted-foreground">
                          CSV should have an "email" column
                        </span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </TabsContent>
            
            {/* Pending Invites Tab */}
            <TabsContent value="pending" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <Label>Pending Invitations</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPendingInvites}
                  disabled={loadingInvites}
                  className="gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingInvites ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                {loadingInvites ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                  </div>
                ) : pendingInvites.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    No pending invitations
                  </div>
                ) : (
                  <div className="divide-y">
                    {pendingInvites.map((invite) => (
                      <div
                        key={invite.invite_token}
                        className="flex items-center justify-between p-3 hover:bg-muted/50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{invite.email}</p>
                          <p className="text-xs text-muted-foreground">
                            Sent {new Date(invite.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvite(invite.invite_token)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend User Modal */}
      <Dialog open={suspendModal.open} onOpenChange={(open) => !savingSuspend && setSuspendModal({ open, userId: null, userName: null, currentUntil: null })}>
        <DialogContent className="sm:max-w-md" data-testid="suspend-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Suspend from Submitting
            </DialogTitle>
            <DialogDescription>
              Set a date until which <span className="font-semibold text-foreground">"{suspendModal.userName}"</span> cannot submit code. They can still browse and use the site.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            {suspendModal.currentUntil && new Date(suspendModal.currentUntil) > new Date() && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-700 dark:text-amber-400">
                Currently suspended until {new Date(suspendModal.currentUntil).toLocaleDateString()}
              </div>
            )}
            <div>
              <Label htmlFor="suspend-until" className="block text-sm font-medium mb-2">Suspended until</Label>
              <Input
                id="suspend-until"
                type="date"
                value={suspendUntil}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setSuspendUntil(e.target.value)}
                className="w-full"
                data-testid="suspend-until-input"
              />
              <p className="mt-2 text-xs text-muted-foreground">Submissions are blocked up to and including this date.</p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            {suspendModal.currentUntil && new Date(suspendModal.currentUntil) > new Date() && (
              <Button
                variant="outline"
                onClick={() => confirmSuspend(true)}
                disabled={savingSuspend}
                className="mr-auto text-green-600 border-green-300 hover:bg-green-50"
                data-testid="lift-suspension-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Lift Suspension
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setSuspendModal({ open: false, userId: null, userName: null, currentUntil: null })}
              disabled={savingSuspend}
            >
              Cancel
            </Button>
            <Button
              onClick={() => confirmSuspend(false)}
              disabled={savingSuspend || !suspendUntil}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-suspend-btn"
            >
              {savingSuspend ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Suspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stripe panel */}
      <StripeSyncDialog
        userId={stripeModal.userId}
        userLabel={stripeModal.userLabel}
        open={stripeModal.open}
        onOpenChange={(open) => setStripeModal((m) => ({ ...m, open }))}
        onChanged={fetchUsers}
      />

      {/* Completed Questions Modal */}
      <Dialog 
        open={completedQuestionsModal.open} 
        onOpenChange={(open) => !open && setCompletedQuestionsModal({ open: false, userId: null, userName: null })}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookCheck className="w-5 h-5 text-emerald-500" />
              Completed Questions
            </DialogTitle>
            <DialogDescription>
              {completedQuestionsModal.userName && (
                <span>
                  Questions completed by <strong>{completedQuestionsModal.userName}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {loadingCompletedQuestions ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : completedQuestionsData ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Summary stats */}
              <div className="flex gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{completedQuestionsData.total_unique_questions}</p>
                  <p className="text-xs text-muted-foreground">Unique Questions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{completedQuestionsData.total_completions}</p>
                  <p className="text-xs text-muted-foreground">Total Completions</p>
                </div>
              </div>
              
              {/* Multi-column questions list */}
              {completedQuestionsData.completed_questions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No completed questions yet
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto pr-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {completedQuestionsData.completed_questions.map((q) => (
                      <div 
                        key={q.question_id}
                        className="flex items-center justify-between gap-2 p-2 bg-card border border-border/40 rounded-lg text-sm"
                        title={`First: ${new Date(q.first_completed).toLocaleDateString()}\nLast: ${new Date(q.last_completed).toLocaleDateString()}`}
                      >
                        <span className="truncate flex-1 text-xs" title={q.question_title}>
                          {q.question_title}
                        </span>
                        <Badge 
                          variant={q.completion_count > 1 ? "default" : "secondary"} 
                          className="shrink-0 text-xs px-1.5 py-0"
                        >
                          {q.completion_count}x
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setCompletedQuestionsModal({ open: false, userId: null, userName: null })}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
